/**
 * WorkflowRunsPage — /agents/workflow-runs (run history)
 * Demo pages: /tables/data-tables (list with status pills, selects and a footer) and /ai (run status, cost, duration
 * as summary numbers). The list endpoint has no query parameters, so the range and filters apply on the client.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, CircleDollarSign, History, ListChecks, XCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  DataTable,
  EmptyState,
  FilterPills,
  SearchBar,
  Toolbar,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { PAGE_HELP } from '../../help/index.js';
import {
  listAgentWorkflowRunsOptions,
  listAgentWorkflowsOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { WorkflowRun } from '../../generated/api/types.gen.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';
import { DEFAULT_RANGE_MS, RUN_RANGES, RunsFilterSelects, type RunRange } from './runs-list/RunsFilterSelects.js';
import { runStatusVariant } from './runs-list/runStatusVariant.js';
import { useNow } from './runs-list/useNow.js';

const isSucceeded = (r: WorkflowRun) => r.status === 'completed' || r.status === 'succeeded';
const isCancelled = (r: WorkflowRun) => r.status === 'cancelled' || r.status === 'canceled';

function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function durationOf(run: WorkflowRun, now: number): string {
  const start = new Date(run.startedAt).getTime();
  if (Number.isNaN(start)) return '—';
  if (run.completedAt) {
    const end = new Date(run.completedAt).getTime();
    if (!Number.isNaN(end) && end >= start) return formatDurationMs(end - start);
  }
  return run.status === 'running' && now > start ? `${formatDurationMs(now - start)}…` : '—';
}

function formatStarted(startedAt: string): string {
  const d = new Date(startedAt);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const formatCost = (run: WorkflowRun) => (run.totalCostUSD == null ? '—' : `$${run.totalCostUSD.toFixed(2)}`);

export const WorkflowRunsPage: React.FC = () => {
  const help = PAGE_HELP['runs'];
  const navigate = useNavigate();
  const [range, setRange] = useState<RunRange>('24h');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [workflowFilter, setWorkflowFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');

  // Polls so running rows trace live.
  const runsQuery = useQuery({ ...listAgentWorkflowRunsOptions(), refetchInterval: 3000 });
  const workflowsQuery = useQuery(listAgentWorkflowsOptions());
  const rows = runsQuery.data?.data ?? [];
  const names = new Map((workflowsQuery.data?.data ?? []).map((w) => [w.id, w.name]));
  const nameOf = (run: WorkflowRun) => names.get(run.workflowId) ?? 'Unnamed workflow';
  const now = useNow(rows.some((r) => r.status === 'running'));

  const rangeMs = RUN_RANGES.find((r) => r.value === range)?.ms ?? DEFAULT_RANGE_MS;
  const inRange = rows.filter((r) => {
    const t = new Date(r.startedAt).getTime();
    return Number.isNaN(t) || t >= Date.now() - rangeMs;
  });
  const term = search.trim().toLowerCase();
  const matches = (r: WorkflowRun) => {
    if (term && !nameOf(r).toLowerCase().includes(term)) return false;
    if (workflowFilter && r.workflowId !== workflowFilter) return false;
    if (triggerFilter && r.triggeredBy !== triggerFilter) return false;
    return true;
  };
  const scoped = inRange.filter(matches);
  const shown = scoped.filter((r) => {
    if (status === 'succeeded') return isSucceeded(r);
    if (status === 'running') return r.status === 'running';
    if (status === 'failed') return r.status === 'failed';
    if (status === 'cancelled') return isCancelled(r);
    return true;
  });

  const count = (pred: (r: WorkflowRun) => boolean) => scoped.filter(pred).length;
  const failedCount = count((r) => r.status === 'failed');
  const runningCount = count((r) => r.status === 'running');
  const totalCost = scoped.reduce((sum, r) => sum + (r.totalCostUSD ?? 0), 0);

  const workflowOptions = [...new Map(inRange.map((r) => [r.workflowId, nameOf(r)])).entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const triggerOptions = [...new Set(inRange.map((r) => r.triggeredBy).filter(Boolean))]
    .sort()
    .map((value) => ({ value, label: sentenceCase(value) }));

  const columns: DataTableColumn<WorkflowRun>[] = [
    {
      key: 'workflow',
      header: 'Workflow',
      width: '34%',
      render: (row) => (
        <Link
          to={`/agents/runs/${row.id}`}
          className="font-medium text-heading transition-colors hover:text-accent-text"
          onClick={(e) => e.stopPropagation()}
        >
          {nameOf(row)}
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const { variant, label } = runStatusVariant(row.status);
        return (
          <Badge variant={variant} dot>
            {label}
          </Badge>
        );
      },
    },
    {
      key: 'trigger',
      header: 'Trigger',
      render: (row) => (
        <span className="text-sm text-muted">{row.triggeredBy ? sentenceCase(row.triggeredBy) : '—'}</span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (row) => <span className="whitespace-nowrap text-sm text-body">{durationOf(row, now)}</span>,
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      sortable: true,
      sortValue: (row) => row.totalCostUSD ?? null,
      render: (row) => <span className="text-sm text-body">{formatCost(row)}</span>,
    },
    {
      key: 'started',
      header: 'Started',
      sortable: true,
      sortValue: (row) => new Date(row.startedAt).getTime(),
      render: (row) => <span className="whitespace-nowrap text-sm text-muted">{formatStarted(row.startedAt)}</span>,
    },
  ];

  const filtered = term !== '' || status !== 'all' || workflowFilter !== '' || triggerFilter !== '';

  return (
    <ListPage
      title="Run history"
      subtitle="Every workflow run with its status, trigger, duration and cost. Open a run for the step timeline and outputs."
      helpContent={help}
      stats={[
        { label: 'Runs', value: scoped.length, icon: ListChecks },
        { label: 'Running', value: runningCount, icon: Activity, severity: runningCount > 0 ? 'accent' : undefined },
        { label: 'Failed', value: failedCount, icon: XCircle, severity: failedCount > 0 ? 'danger' : undefined },
        { label: 'Cost', value: `$${totalCost.toFixed(2)}`, icon: CircleDollarSign },
      ]}
      alert={
        runsQuery.isError ? (
          <Callout
            tone="danger"
            title={describeQueryError(runsQuery.error, 'the run history')}
            actions={
              <Button size="sm" variant="secondary" onClick={() => runsQuery.refetch()}>
                Retry
              </Button>
            }
          />
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search by workflow name…" />}
          pills={
            <FilterPills
              aria-label="Run status"
              value={status}
              onChange={setStatus}
              options={[
                { id: 'all', label: 'All', count: scoped.length },
                { id: 'succeeded', label: 'Succeeded', count: count(isSucceeded) },
                { id: 'running', label: 'Running', count: runningCount },
                { id: 'failed', label: 'Failed', count: failedCount },
                { id: 'cancelled', label: 'Cancelled', count: count(isCancelled) },
              ]}
            />
          }
          right={
            <RunsFilterSelects
              range={range}
              workflowId={workflowFilter}
              triggeredBy={triggerFilter}
              workflowOptions={workflowOptions}
              triggerOptions={triggerOptions}
              onRangeChange={setRange}
              onWorkflowChange={setWorkflowFilter}
              onTriggerChange={setTriggerFilter}
            />
          }
        />
      }
    >
      <DataTable
        aria-label="Workflow runs"
        columns={columns}
        rows={shown}
        rowKey={(row) => row.id}
        loading={runsQuery.isPending}
        pageSize={25}
        getRowAttention={(row) => (row.status === 'failed' ? 'danger' : undefined)}
        onRowClick={(row) => navigate(`/agents/runs/${row.id}`)}
        empty={
          filtered ? (
            <EmptyState
              icon={XCircle}
              title="No runs match"
              description="Change the search or clear a filter to see more runs."
            />
          ) : (
            <EmptyState
              icon={History}
              title="No runs in this window"
              description="Nothing has run in this time range. Widen the range, or trigger a workflow to see its trace here."
              learnItems={help?.emptyState?.learnItems}
              action={
                range !== '30d' ? (
                  <Button variant="secondary" onClick={() => setRange('7d')}>
                    Show the last 7 days
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
    </ListPage>
  );
};
